import { createSelector } from 'reselect';
import moment from 'moment';
import { selectCurrentUser as currentUser } from '../current-user/selectors';
import { selectors as groupSelectors } from '../groups';
import createMetadataAPISelectors from '../../utils/api-metadata-selectors';

const defaultSelectors = createMetadataAPISelectors({
  name: 'memberships',
  defaultSortBy: 'display_order'
});

const allGroups = groupSelectors.byId;

export const activeMemberships = createSelector(defaultSelectors.byId, membershipList => {
  const result = {};
  for (const membership of Object.values(membershipList)) {
    if (membership.status === 'active') {
      result[membership.id] = membership;
    }
  }
  return result;
});

export const activeOrFutureMemberships = createSelector(defaultSelectors.byId, membershipList => {
  const result = {};
  const now = moment();
  for (const membership of Object.values(membershipList)) {
    if (!membership.active_until || now.isBefore(membership.active_until)) {
      result[membership.id] = membership;
    }
  }
  return result;
});

// returns a hash from user_id -> list of memberships that belong to that user
export const membershipsPerUser = createSelector(activeOrFutureMemberships, allGroups, (membershipList, groups) => {
  const result = {};
  for (const membership of Object.values(membershipList)) {
    result[membership.user_id] = result[membership.user_id] || [];
    result[membership.user_id].push({
      ...membership,
      group: groups[membership.group_id]
    });
  }
  return result;
});

// The station memberships of the user, including team memberships
export const userMembershipTree = createSelector(currentUser, allGroups, (currentUser, groupsById) => {
  const userMemberships = currentUser.memberships || [];
  return userMemberships
    .map(membership => ({
      membership,
      group: groupsById[membership.group_id]
    }))
    .filter(({ group }) => group && group.type === 'station')
    .map(({ membership, group }) => ({
      ...membership,
      group,
      descendantMemberships: (group.descendant_ids || [])
        .map(id => userMemberships.find(m => m.group_id === id))
        .filter(Boolean)
        .map(m => ({ ...m, group: groupsById[m.group_id] }))
    }));
});

export const currentUserPlannableStationMemberships = createSelector(
  currentUser,
  allGroups,
  (currentUser, groupsById) =>
    (currentUser?.memberships || [])
      .filter(m => m.plannable)
      .map(m => ({ ...m, group: groupsById[m.group_id] }))
      .filter(m => m.group)
      .filter(m => m.group.type === 'station')
);

// returns a list of memberships that belong to the current user and are
// plannable and the station has the on_call_planning feature enabled. This is
// used in the availability change form to show the list of stations that the
// user can change their availability for.
export const currentUserPlannableOnCallStationMemberships = createSelector(
  currentUserPlannableStationMemberships,
  memberships => memberships.filter(m => (m.group.enabled_features || []).includes('on_call_planning'))
);

export default {
  ...defaultSelectors,
  activeMemberships,
  membershipsPerUser,
  currentUserPlannableStationMemberships,
  currentUserPlannableOnCallStationMemberships,
  userMembershipTree
};
